import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { HttpService } from '../../shared/http-service.class';
import { IEnrollmentScheduleRequest } from '../interfaces/ienrollment-schedule.interface';
import { MyLBApiConfiguration } from '../mylb-api.configuration';
import { InstructorWeeklyGridResult } from '../interfaces/instructor-weekly-grid.interface';
import { IEnrollmentSchedulingSummary, IEnrollmentSchedulingSummaryNormalized } from '../interfaces/ienrollment-scheduling-summary.interface';
import { ISimpleInstructorMatchResult } from '../interfaces/isimple-instructor-match.interface';

@Injectable({
	providedIn: 'root'
})
export class EnrollmentSchedulingService extends HttpService {
	constructor(config: MyLBApiConfiguration) {
		super(config, 'EnrollmentScheduling');
	}

	private convertSlotsToDayMap(slots: { startTime: string; endTime?: string; dayIndex: number; timezoneID?: number }[]): {
		[dayIndex: number]: Array<{ startTime: string; endTime?: string; timezoneID?: number }>;
	} {
		const map: { [dayIndex: number]: Array<{ startTime: string; endTime?: string; timezoneID?: number }> } = {};
		slots?.forEach((slot) => {
			const day = slot.dayIndex;
			if (!map[day]) map[day] = [];
			map[day].push({ startTime: slot.startTime, endTime: slot.endTime, timezoneID: slot.timezoneID });
		});
		Object.keys(map).forEach((k) => (map[+k] = map[+k].sort((a, b) => (a.startTime < b.startTime ? -1 : a.startTime > b.startTime ? 1 : 0))));
		return map;
	}

	getEnrollmentSchedulingSummaryNormalized(enrollmentID: number): Observable<IEnrollmentSchedulingSummaryNormalized> {
		return this.get<IEnrollmentSchedulingSummary>(`${this.serviceUrl}/LearnerSchedulingSummary/${enrollmentID}`).pipe(
			map((raw) => {
				const availabilitySlotDayMap = this.convertSlotsToDayMap(raw.availabilities);
				const scheduleSlotDayMap = this.convertSlotsToDayMap(raw.schedules);
				return {
					enrollmentID: raw.enrollmentID,
					availabilitySlotDayMap,
					scheduleSlotDayMap,
					availabilities: raw.availabilities,
					schedules: raw.schedules
				};
			})
		);
	}

	getInstructorWeeklyGrid(
		enrollmentId: number,
		instructorUserID: number,
		startDate: Date | string,
		endDate: Date | string
	): Observable<InstructorWeeklyGridResult> {
		const start = typeof startDate === 'string' ? startDate : startDate.toISOString();
		const end = typeof endDate === 'string' ? endDate : endDate.toISOString();
		return this.get<InstructorWeeklyGridResult>(`${this.serviceUrl}/InstructorAvailabilityGrid/${enrollmentId}/${instructorUserID}/${start}/${end}`);
	}

	saveEnrollmentSchedule(enrollmentID: number, enrollmentScheduleRequest: IEnrollmentScheduleRequest): Observable<boolean> {
		return this.post<boolean>(`${this.serviceUrl}/SaveSchedule/${enrollmentID}`, enrollmentScheduleRequest);
	}

	getSimpleInstructorMatches(enrollmentId: number, windowStartUtc?: Date | string, windowEndUtc?: Date | string): Observable<ISimpleInstructorMatchResult> {
		let url = `${this.serviceUrl}/InstructorAvailability/${enrollmentId}`;
		if (windowStartUtc && windowEndUtc) {
			const start = typeof windowStartUtc === 'string' ? windowStartUtc : windowStartUtc.toISOString();
			const end = typeof windowEndUtc === 'string' ? windowEndUtc : windowEndUtc.toISOString();
			url += `?windowStartUtc=${encodeURIComponent(start)}&windowEndUtc=${encodeURIComponent(end)}`;
		}
		// HttpClient automatically converts PascalCase to camelCase, so we can directly return the response
		return this.get<ISimpleInstructorMatchResult>(url);
	}
}
